import { TwoUIBiCustomClick, useTwoUITelemetry } from "@amx/ux-twoui-telemetry";
import { Palette, SemanticColors, useTheme } from "@amx/ux-twoui-theming";
import {
    ContextualMenuItemType, DirectionalHint, IButtonProps, IButtonStyles,
    IconButton as FluentIconButton, IContextualMenuItem, IContextualMenuStyles
} from "@fluentui/react";
import React from "react";
import { useComponentLogger } from "../../../shared/logging";
import { InteractiveElementProps } from "../../../shared/props";
import { getColorFromCorrectPalette } from "../../../styles/colors";
import { Stack } from "../../Stacks/Stack/Stack";
import { Text } from "../../Text/Text";
import { TooltipHost, TooltipHostProps } from "../../TooltipHost/TooltipHost";
import { BaseIconButtonProps } from "../shared/props";

export interface MenuItemProps extends InteractiveElementProps {
    /** Unique id to identify the item */
    readonly key: string;

    /** Text description for the menu item to display */
    readonly name: string;

    /** Callback for when the menu item is invoked */
    readonly onClick?: (ev?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => boolean | void;

    /** Method to custom render this menu item. */
    readonly onRender?: (item: any, dismissMenu: (ev?: any, dismissAll?: boolean) => void) => JSX.Element;

    /** Type of menu item */
    readonly itemType?: "normal";
}

export interface MenuSeparatorProps {
    /** Type of menu item */
    readonly itemType: "divider";
}

export interface MenuHeaderProps {
    /** Type of menu item */
    readonly itemType: "header";

    /** Unique id to identify the item */
    readonly key: string;

    /** Text description for the menu item to display */
    readonly name: string;

    /** Background color of the header */
    readonly backgroundColor?: MenuBackgroundColor;
}

export type MenuBackgroundColor = Extract<keyof (SemanticColors & Palette), "neutralMed" | "neutralHigh" | "neutralLight">;

export type MenuItem = MenuItemProps | MenuSeparatorProps | MenuHeaderProps;

export interface MenuIconButtonProps extends BaseIconButtonProps, InteractiveElementProps {
    /** Props for button menu. */
    readonly menuItems: MenuItem[]; // tslint:disable-line: readonly-array

    /** If true then the beak is visible. If false it will not be shown. */
    readonly isBeakVisible?: boolean;

    /** Background color of the Menu */
    readonly backgroundColor?: MenuBackgroundColor;

    /** Callback for when the menu is opened */
    readonly onMenuOpened?: () => void;

    /** Callback for when the menu is dismissed */
    readonly onMenuDismissed?: () => void;

    /** Padding to be applied to the MenuItem contents relative to the menu container. */
    readonly menuItemsPadding?: number | string;

    /** override style when showing in different components. */
    readonly menuIconVariant?: MenuIconVariant;

    /** Props for tooltip */
    readonly tooltipHostProps?: TooltipHostProps;
}

export type MenuIconVariant = "card" | "default";

export const MenuIconButton: React.FC<MenuIconButtonProps> = ({
    iconName,
    menuItems,
    iconColor,
    isBeakVisible,
    backgroundColor,
    onMenuOpened,
    onMenuDismissed,
    menuItemsPadding,
    disabled,
    hasHighContrastBackground,
    menuIconVariant = "default",
    ...otherProps
}) => {
    useComponentLogger("MenuIconButton", otherProps.id);
    const theme = useTheme();
    const twoUITelemetry = useTwoUITelemetry();
    const pageArea = twoUITelemetry.twoUITelemetryIds.areaBiId;

    const doNotRenderMenuIcon = () => null;

    const updatedMenuItems: IContextualMenuItem[] = menuItems.map((item, index) => {
        if (item.itemType === "divider") {
            return { key: `divider_${index}`, text: "", itemType: ContextualMenuItemType.Divider };
        } else if (item.itemType === "header") {
            return {
                ...item,
                style: item.backgroundColor ? { backgroundColor: getColorFromCorrectPalette(theme, item.backgroundColor) } : {},
                itemType: ContextualMenuItemType.Header
            };
        } else {
            const disabledRenderItem = () => <Stack padding="8px 8p 4px 8px"><Text disabled={true}>{item.name}</Text></Stack>;

            const addReportClick = (event: React.MouseEvent<HTMLElement, MouseEvent> | React.KeyboardEvent<HTMLElement> | undefined) => {
                // create the custom click event
                const clickEvent: TwoUIBiCustomClick = {
                    clickId: `${item["data-bi-id"]}`,
                    pageArea: pageArea
                };
                // send the event
                twoUITelemetry.reportClick(clickEvent);

                // run the original onClick function
                if (item.onClick) {
                    item.onClick(event);
                }
            };

            return { ...item, ariaLabel: item["aria-label"], itemType: ContextualMenuItemType.Normal, onRender: disabled ? disabledRenderItem : item.onRender, onClick: addReportClick };
        }
    });

    const menuStyles: Partial<IContextualMenuStyles> = { root: { padding: menuItemsPadding, backgroundColor: backgroundColor ? getColorFromCorrectPalette(theme, backgroundColor) : undefined } };

    const buttonProps: IButtonProps = {
        iconProps: { iconName },
        menuProps: {
            items: updatedMenuItems,
            isBeakVisible,
            styles: menuStyles,
            directionalHint: DirectionalHint.bottomRightEdge,
            gapSpace: 4,
            onMenuOpened,
            onMenuDismissed
        }
    };

    const iconStyle: IButtonStyles = { icon: {} };

    if (menuIconVariant === "card") {
        iconStyle.root = {
            ...iconStyle.root as IButtonStyles,
            padding: "22px 10px 22px 10px"
        };
    }

    if (iconColor) {
        iconStyle.icon!["color"] = getColorFromCorrectPalette(theme, iconColor);
    }

    if (hasHighContrastBackground) {
        iconStyle.rootHovered = { backgroundColor: theme.palette.neutralLight };
        iconStyle.rootPressed = { backgroundColor: theme.palette.neutralQuaternaryAlt };
    }

    if (otherProps.tooltipHostProps) {
        return (
            <TooltipHost {...otherProps.tooltipHostProps}>
                <FluentIconButton
                    {...buttonProps}
                    {...otherProps}
                    onRenderMenuIcon={doNotRenderMenuIcon}
                    styles={iconStyle}
                />
            </TooltipHost>
        );
    }

    return (
        <FluentIconButton
            {...buttonProps}
            {...otherProps}
            onRenderMenuIcon={doNotRenderMenuIcon}
            styles={iconStyle}
        />
    );
};
